import { Link } from "react-router-dom";
import { blogPosts, blogCategories } from "../../../mocks/blog";
import { categoryToSlug } from "../../../utils/blogSlug";

const categoryMeta: Record<string, { icon: string; image: string; description: string }> = {
  "Product Sourcing": {
    icon: "ri-search-eye-line",
    description: "Factory vetting, QC, supplier negotiation, OEM/ODM strategy",
    image: "https://public.readdy.ai/ai/img_res/395d3614eb6855c714ccafa72053a94d.jpg",
  },
  Wholesale: {
    icon: "ri-store-2-line",
    description: "MOQ strategy, margin optimisation, import channel development",
    image: "https://readdy.ai/api/search-image?query=large%20organised%20wholesale%20warehouse%20rows%20of%20product%20pallets%20modern%20distribution%20centre%20bright%20lighting%20inventory%20management%20clean%20facility&width=600&height=400&seq=cat-wholesale&orientation=landscape",
  },
  Inventory: {
    icon: "ri-stack-line",
    description: "Overstock clearance, seasonal planning, liquidation strategy",
    image: "https://readdy.ai/api/search-image?query=inventory%20management%20warehouse%20shelves%20organised%20products%20stock%20control%20modern%20clean%20storage%20facility%20efficient%20logistics%20neutral%20tones&width=600&height=400&seq=cat-inventory&orientation=landscape",
  },
  Logistics: {
    icon: "ri-ship-line",
    description: "Incoterms, freight options, customs, landed cost optimisation",
    image: "https://readdy.ai/api/search-image?query=cargo%20port%20container%20ship%20aerial%20view%20international%20shipping%20logistics%20harbour%20cranes%20freight%20ocean%20trade%20warm%20golden%20tones&width=600&height=400&seq=cat-logistics&orientation=landscape",
  },
  "Supply Chain": {
    icon: "ri-node-tree",
    description: "Resilience, dual-sourcing, nearshoring, RFQ optimisation",
    image: "https://readdy.ai/api/search-image?query=global%20supply%20chain%20network%20map%20business%20intelligence%20analytics%20modern%20office%20digital%20screens%20procurement%20strategy%20warm%20professional%20environment&width=600&height=400&seq=cat-supplychain&orientation=landscape",
  },
};

export default function CategoryShowcase() {
  const counts: Record<string, number> = {};
  blogCategories.slice(1).forEach((cat) => {
    counts[cat] = blogPosts.filter((p) => p.category === cat).length;
  });

  return (
    <section className="py-16 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-10">
          <div>
            <span className="text-xs font-bold uppercase tracking-widest text-[#C9A227] mb-2 block">
              Browse by Topic
            </span>
            <h2 className="text-2xl lg:text-3xl font-extrabold text-[#222831]">
              Find What You Need Faster
            </h2>
          </div>
          <Link
            to="/blog"
            className="text-sm font-semibold text-[#3A506B] hover:text-[#C9A227] transition-colors flex items-center gap-1 cursor-pointer whitespace-nowrap"
          >
            View all articles
            <i className="ri-arrow-right-line"></i>
          </Link>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
          {blogCategories.slice(1).map((cat) => {
            const meta = categoryMeta[cat];
            return (
              <Link
                key={cat}
                to={`/blog/category/${categoryToSlug(cat)}`}
                className="group relative rounded-xl overflow-hidden text-left cursor-pointer h-56 focus:outline-none block"
              >
                {/* BG Image */}
                <div className="absolute inset-0 w-full h-full">
                  <img
                    src={meta.image}
                    alt={cat}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#222831]/90 via-[#222831]/40 to-transparent"></div>
                </div>

                {/* Content */}
                <div className="relative z-10 h-full flex flex-col justify-end p-4">
                  <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-[#C9A227] mb-2">
                    <i className={`${meta.icon} text-sm text-white`}></i>
                  </div>
                  <h3 className="text-sm font-bold text-white leading-tight mb-1">{cat}</h3>
                  <p className="text-xs text-white/60 leading-snug line-clamp-2">{meta.description}</p>
                  <div className="mt-2 flex items-center gap-1">
                    <span className="text-xs font-bold text-[#C9A227]">{counts[cat]}</span>
                    <span className="text-xs text-white/50">articles</span>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
}
